/**
 * IconPicker — F12.A2 T12.A2.4.
 *
 * Grid 100+ Lucide ikon z search. Wybór ikony dla modułu / pola.
 * Renderowany jako popover/dropdown — wzorzec z SellCall (`IconPicker.tsx` w fields/types).
 *
 * Użycie:
 *   <IconPicker value={iconName} onChange={(name) => ...} />
 *
 * Domyślnie ograniczone do ~80 najczęściej używanych Lucide ikon (subset). Z `fullLibrary`
 * pokazuje pełny zestaw (~1500, lib/lucide-all-icons.ts) — cała biblioteka i tak jest w bundlu.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  AlertCircle,
  Archive,
  AtSign,
  Award,
  BarChart3,
  Bell,
  Book,
  Bookmark,
  Box,
  Briefcase,
  Building,
  Building2,
  Calendar,
  Camera,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clipboard,
  Clock,
  Cloud,
  Code,
  Coffee,
  Compass,
  Copy,
  CreditCard,
  Database,
  DollarSign,
  Download,
  Edit,
  Euro,
  Eye,
  File,
  FileText,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  Hash,
  Headphones,
  Heart,
  Home,
  Image as ImageIcon,
  Inbox,
  Info,
  Key,
  Layers,
  Lightbulb,
  Link,
  List,
  type LucideIcon,
  Mail,
  Map,
  MapPin,
  MessageSquare,
  Mic,
  Monitor,
  Music,
  Package,
  Paperclip,
  Phone,
  PieChart,
  Plus,
  Receipt,
  Rocket,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Star,
  Sun,
  Tag,
  Target,
  Trash2,
  Truck,
  User,
  Users,
  Video,
  Wallet,
  Wrench,
  Zap,
} from 'lucide-react';

import { ALL_LUCIDE_ICONS } from '@/lib/lucide-all-icons';

const ICON_MAP: Record<string, LucideIcon> = {
  Activity,
  AlertCircle,
  Archive,
  AtSign,
  Award,
  BarChart3,
  Bell,
  Book,
  Bookmark,
  Box,
  Briefcase,
  Building,
  Building2,
  Calendar,
  Camera,
  Car,
  Check,
  CheckCircle2,
  ChevronDown,
  Circle,
  Clipboard,
  Clock,
  Cloud,
  Code,
  Coffee,
  Compass,
  Copy,
  CreditCard,
  Database,
  DollarSign,
  Download,
  Edit,
  Euro,
  Eye,
  File,
  FileText,
  Filter,
  Flag,
  Folder,
  Gift,
  Globe,
  Hash,
  Headphones,
  Heart,
  Home,
  Image: ImageIcon,
  Inbox,
  Info,
  Key,
  Layers,
  Lightbulb,
  Link,
  List,
  Mail,
  Map,
  MapPin,
  MessageSquare,
  Mic,
  Monitor,
  Music,
  Package,
  Paperclip,
  Phone,
  PieChart,
  Plus,
  Receipt,
  Rocket,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Star,
  Sun,
  Tag,
  Target,
  Trash2,
  Truck,
  User,
  Users,
  Video,
  Wallet,
  Wrench,
  Zap,
};

interface IconPickerProps {
  value: string;
  onChange: (name: string) => void;
  /** Kolor accent dla wybranej ikony (np. kolor modułu) */
  accentColor?: string;
  /** Custom button label/aria */
  ariaLabel?: string;
  /** Dodatkowe klasy na trigger (np. wyrównanie wysokości do sąsiedniego inputa) */
  triggerClassName?: string;
  /**
   * Pełny zestaw ikon lucide (~1500) zamiast kuratowanej mapy (~80) — używane przez Kreator menu.
   */
  fullLibrary?: boolean;
}

export function IconPicker({
  value,
  onChange,
  accentColor,
  ariaLabel,
  triggerClassName,
  fullLibrary = false,
}: IconPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  // Pełny zestaw (Kreator menu) albo kuratowana mapa (~80) — reszta aplikacji.
  const iconMap: Record<string, LucideIcon> = fullLibrary ? ALL_LUCIDE_ICONS : ICON_MAP;

  const SelectedIcon = iconMap[value] ?? ICON_MAP[value] ?? Database;

  const filteredIcons = useMemo(() => {
    const names = Object.keys(iconMap);
    if (!search.trim()) return names;
    const q = search.toLowerCase();
    return names.filter((n) => n.toLowerCase().includes(q));
  }, [search, iconMap]);

  function openDropdown() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      const margin = 8;
      const width = Math.max(rect.width, 288);
      // Domyślnie wyrównaj do prawej krawędzi triggera, ale przytnij do viewportu,
      // żeby popup nie wychodził poza ekran przy przyciskach blisko prawej krawędzi.
      const maxLeft = window.innerWidth - width - margin;
      const left = Math.max(margin, Math.min(rect.left, maxLeft));
      // Pionowo: domyślnie pod triggerem, ale jeśli nie ma miejsca w dół,
      // a w górę jest więcej — otwórz nad triggerem. Wysokość przycinamy do
      // dostępnej przestrzeni, żeby siatka ikon nie uciekała poza ekran.
      const desired = 360;
      const spaceBelow = window.innerHeight - rect.bottom - margin;
      const spaceAbove = rect.top - margin;
      if (spaceBelow >= desired || spaceBelow >= spaceAbove) {
        // Pod triggerem — kotwiczymy górną krawędź, wysokość do dołu ekranu.
        setPos({ top: rect.bottom + 4, left, width, maxHeight: Math.max(160, spaceBelow - 4) });
      } else {
        // Nad triggerem — kotwiczymy dolną krawędź tuż nad triggerem; popup
        // rośnie w górę, ograniczony maxHeight, więc nie ucieka poza ekran.
        setPos({
          bottom: window.innerHeight - rect.top + 4,
          left,
          width,
          maxHeight: Math.max(160, spaceAbove - 4),
        });
      }
    }
    setSearch('');
    setOpen(true);
  }

  // Zamykaj na klik poza
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    // Zamykaj przy scrollu (np. modal się przesuwa) — ale NIE przy scrollu własnej siatki ikon
    // (faza przechwytywania łapie też scroll wewnątrz panelu, co zamykało listę przy przewijaniu).
    const onScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    // Esc zamyka tylko listę: faza przechwytywania + `preventDefault`, więc okno nakładkowe
    // (`Overlay`), w którym stoi picker, widzi Esc jako obsłużony i się nie zamyka.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      setOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('scroll', onScroll, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  const dropdown =
    open && pos ? (
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: pos.top,
          bottom: pos.bottom,
          left: pos.left,
          width: pos.width,
          maxHeight: pos.maxHeight,
          zIndex: 9999,
        }}
        className="flex flex-col rounded-md border border-border-primary bg-bg-primary p-2 shadow-xl"
        data-testid="icon-picker-grid"
      >
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('tenant.modules.iconPicker.search')}
          className="mb-2 w-full rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
          autoFocus
        />
        <div
          className="grid min-h-0 flex-1 grid-cols-7 gap-1 overflow-y-auto overflow-x-hidden"
          role="listbox"
        >
          {filteredIcons.map((name) => {
            const Icon = iconMap[name]!;
            const selected = name === value;
            return (
              <button
                key={name}
                type="button"
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                role="option"
                aria-selected={selected}
                title={name}
                data-testid={`icon-picker-icon-${name}`}
                className={
                  'flex h-8 w-8 items-center justify-center rounded transition ' +
                  (selected
                    ? 'bg-accent-500/10 text-accent-600 ring-2 ring-accent-500'
                    : 'text-text-secondary hover:bg-bg-tertiary hover:text-text-primary')
                }
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
          {filteredIcons.length === 0 && (
            <p className="col-span-7 py-3 text-center text-xs text-text-muted">
              {t('tenant.modules.iconPicker.notFound')}
            </p>
          )}
        </div>
      </div>
    ) : null;

  return (
    <div className="relative" data-testid="icon-picker">
      <button
        ref={triggerRef}
        type="button"
        onClick={openDropdown}
        aria-label={ariaLabel ?? t('tenant.modules.iconPicker.open')}
        aria-expanded={open}
        className={[
          'flex items-center gap-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary hover:border-accent-500',
          triggerClassName ?? '',
        ].join(' ')}
        data-testid="icon-picker-trigger"
      >
        <span
          className="flex h-7 w-7 items-center justify-center rounded"
          style={
            accentColor ? { backgroundColor: `${accentColor}20`, color: accentColor } : undefined
          }
        >
          <SelectedIcon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="text-xs">{value}</span>
        <ChevronDown className="h-3 w-3 text-text-muted" aria-hidden="true" />
      </button>

      {createPortal(dropdown, document.body)}
    </div>
  );
}

/**
 * Ikona z mapy pickera po nazwie — undefined gdy nazwy nie ma w mapie. Wyświetlanie ikony
 * wybranej w pickerze musi iść tą samą mapą (mapy w sidebarze/`icon-map` są mniejsze).
 */
export function resolvePickerIcon(name: string | null | undefined): LucideIcon | undefined {
  return name ? ICON_MAP[name] : undefined;
}

/** Helper: render Lucide icon by name (fallback Database). */
export function renderIconByName(
  name: string | null | undefined,
  className?: string,
  strokeWidth?: number,
) {
  const Icon = (name && ICON_MAP[name]) || Database;
  return <Icon className={className ?? 'h-5 w-5'} strokeWidth={strokeWidth} aria-hidden="true" />;
}

export const ICON_NAMES = Object.keys(ICON_MAP);
